"use client"

import { useState, useEffect, useRef } from "react"
import axios from "axios"
import { API_URL } from "../../config"
import { useLanguage } from "../../context/LanguageContext"
import { useNotification } from "../../context/NotificationContext"
import "./MasterData.css"

const MasterData = () => {
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const [activeTab, setActiveTab] = useState("countries")
  const [countries, setCountries] = useState([])
  const [districts, setDistricts] = useState([])
  const [soilTypes, setSoilTypes] = useState([])
  const [waterSources, setWaterSources] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)
  const [plants, setPlants] = useState([])
  const [plantTypes, setPlantTypes] = useState([])
  const [cropTypes, setCropTypes] = useState([])
  const [searchTerm, setSearchTerm] = useState("");
const filteredCountries = countries.filter(country =>
  country.name.toLowerCase().includes(searchTerm.toLowerCase())
);
const [searchDistrict, setSearchDistrict] = useState("");
const filteredDistricts = districts.filter(d =>
  d.name.toLowerCase().includes(searchDistrict.toLowerCase())
);

  // Form states
  const [countryForm, setCountryForm] = useState({ name: "", code: "" })
  const [districtForm, setDistrictForm] = useState({
    name: "",
    country: "",
    coordinates: [0, 0],
  })
  const [soilTypeForm, setSoilTypeForm] = useState({
    name: "",
    description: "",
    properties: {
      texture: "loamy",
      drainageRate: "moderate",
      waterHoldingCapacity: "medium",
      organicMatterContent: "medium",
    },
  })
  const [waterSourceForm, setWaterSourceForm] = useState({
    name: "",
    description: "",
    type: "surface",
    properties: {
      quality: "good",
      reliability: "reliable",
      seasonality: "year-round",
    },
  })
  const [plantForm, setPlantForm] = useState({
    name: "",
    scientificName: "",
    description: "",
    plantType: "",
    waterRequirements: "medium",
    growthCycle: "annual",
    region: "",
    plantMonth: "", // Changed from plantDate
    cropCoefficients: {
      initial: 0.3,
      mid: 1.0,
      end: 0.7,
    },
    growthStages: {
      initialDays: 20,
      developmentDays: 30,
      midSeasonDays: 40,
      lateSeasonDays: 30,
    },
    totalDays: 120,
  })
  const [cropTypeForm, setCropTypeForm] = useState({
    name: "",
    code: "",
    description: "",
  })

  // Editing states
  const [editingId, setEditingId] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  // Fetch data on component mount and when active tab changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        switch (activeTab) {
          case "countries":
            const countriesRes = await axios.get(`${API_URL}/api/locations/countries`)
            setCountries(countriesRes.data)
            break
          case "districts":
            const countriesForDistrictsRes = await axios.get(`${API_URL}/api/locations/countries`)
            setCountries(countriesForDistrictsRes.data)
            const districtsRes = await axios.get(`${API_URL}/api/locations/districts/all`)
            setDistricts(districtsRes.data)
            break
          case "soilTypes":
            const soilTypesRes = await axios.get(`${API_URL}/api/soil-types`)
            setSoilTypes(soilTypesRes.data)
            break
          case "waterSources":
            const waterSourcesRes = await axios.get(`${API_URL}/api/water-sources`)
            setWaterSources(waterSourcesRes.data)
            break
          case "cropTypes":
            const cropTypesRes = await axios.get(`${API_URL}/api/crop-types`)
            setCropTypes(cropTypesRes.data)
            break
          case "plants":
            const plantsRes = await axios.get(`${API_URL}/api/crops`)
            setPlants(plantsRes.data)

            // Fetch plant types for the dropdown
            const plantTypesRes = await axios.get(`${API_URL}/api/crop-types`)
            setPlantTypes(plantTypesRes.data)
            break
          default:
            break
        }

        setLoading(false)
      } catch (err) {
        console.error(err)
        setError("Failed to load data")
        setLoading(false)
      }
    }

    fetchData()
  }, [activeTab])

  // Handle form changes
  const handleCountryChange = (e) => {
    setCountryForm({ ...countryForm, [e.target.name]: e.target.value })
  }

  const handleDistrictChange = (e) => {
    if (e.target.name === "latitude" || e.target.name === "longitude") {
      const coordinates = [...districtForm.coordinates]
      if (e.target.name === "longitude") {
        coordinates[0] = Number(e.target.value)
      } else {
        coordinates[1] = Number(e.target.value)
      }
      setDistrictForm({ ...districtForm, coordinates })
    } else {
      setDistrictForm({ ...districtForm, [e.target.name]: e.target.value })
    }
  }

  const handleSoilTypeChange = (e) => {
    const { name, value } = e.target

    if (name.startsWith("properties.")) {
      const property = name.split(".")[1]
      setSoilTypeForm({
        ...soilTypeForm,
        properties: {
          ...soilTypeForm.properties,
          [property]: value,
        },
      })
    } else {
      setSoilTypeForm({ ...soilTypeForm, [name]: value })
    }
  }

  const handleWaterSourceChange = (e) => {
    const { name, value } = e.target

    if (name.startsWith("properties.")) {
      const property = name.split(".")[1]
      setWaterSourceForm({
        ...waterSourceForm,
        properties: {
          ...waterSourceForm.properties,
          [property]: value,
        },
      })
    } else {
      setWaterSourceForm({ ...waterSourceForm, [name]: value })
    }
  }

  const handlePlantChange = (e) => {
    const { name, value } = e.target

    if (name.startsWith("cropCoefficients.")) {
      const property = name.split(".")[1]
      setPlantForm({
        ...plantForm,
        cropCoefficients: {
          ...plantForm.cropCoefficients,
          [property]: Number(value),
        },
      })
    } else if (name.startsWith("growthStages.")) {
      const property = name.split(".")[1]
      const updatedGrowthStages = {
        ...plantForm.growthStages,
        [property]: Number(value),
      }

      // Calculate total days
      const totalDays =
        updatedGrowthStages.initialDays +
        updatedGrowthStages.developmentDays +
        updatedGrowthStages.midSeasonDays +
        updatedGrowthStages.lateSeasonDays

      setPlantForm({
        ...plantForm,
        growthStages: updatedGrowthStages,
        totalDays: totalDays,
      })
    } else {
      setPlantForm({ ...plantForm, [name]: value })
    }
  }

  const handleCropTypeChange = (e) => {
    setCropTypeForm({ ...cropTypeForm, [e.target.name]: e.target.value })
  }

  // Handle form submissions
  const handleCountrySubmit = async (e) => {
    e.preventDefault()

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/locations/countries/${editingId}`, countryForm)
        showNotification(t("Country updated successfully"), "success")
      } else {
        await axios.post(`${API_URL}/api/locations/countries`, countryForm)
        showNotification(t("Country added successfully"), "success")
      }

      // Refresh countries
      const res = await axios.get(`${API_URL}/api/locations/countries`)
      setCountries(res.data)

      // Reset form
      setCountryForm({ name: "", code: "" })
      setIsEditing(false)
      setEditingId(null)
    } catch (err) {
      console.error(err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const handleDistrictSubmit = async (e) => {
    e.preventDefault()

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/locations/districts/${editingId}`, districtForm)
        showNotification(t("District updated successfully"), "success")
      } else {
        await axios.post(`${API_URL}/api/locations/districts`, districtForm)
        showNotification(t("District added successfully"), "success")
      }

      // Refresh districts
      const res = await axios.get(`${API_URL}/api/locations/districts/all`)
      setDistricts(res.data)

      // Reset form
      setDistrictForm({ name: "", country: "", coordinates: [0, 0] })
      setIsEditing(false)
      setEditingId(null)
    } catch (err) {
      console.error(err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const handleSoilTypeSubmit = async (e) => {
    e.preventDefault()

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/soil-types/${editingId}`, soilTypeForm)
        showNotification(t("Soil type updated successfully"), "success")
      } else {
        await axios.post(`${API_URL}/api/soil-types`, soilTypeForm)
        showNotification(t("Soil type added successfully"), "success")
      }

      // Refresh soil types
      const res = await axios.get(`${API_URL}/api/soil-types`)
      setSoilTypes(res.data)

      // Reset form
      setSoilTypeForm({
        name: "",
        description: "",
        properties: {
          texture: "loamy",
          drainageRate: "moderate",
          waterHoldingCapacity: "medium",
          organicMatterContent: "medium",
        },
      })
      setIsEditing(false)
      setEditingId(null)
    } catch (err) {
      console.error(err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const handleWaterSourceSubmit = async (e) => {
    e.preventDefault()

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/water-sources/${editingId}`, waterSourceForm)
        showNotification(t("Water source updated successfully"), "success")
      } else {
        await axios.post(`${API_URL}/api/water-sources`, waterSourceForm)
        showNotification(t("Water source added successfully"), "success")
      }

      // Refresh water sources
      const res = await axios.get(`${API_URL}/api/water-sources`)
      setWaterSources(res.data)

      // Reset form
      setWaterSourceForm({
        name: "",
        description: "",
        type: "surface",
        properties: {
          quality: "good",
          reliability: "reliable",
          seasonality: "year-round",
        },
      })
      setIsEditing(false)
      setEditingId(null)
    } catch (err) {
      console.error(err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const handlePlantSubmit = async (e) => {
    e.preventDefault()

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/crops/${editingId}`, plantForm)
        showNotification(t("Plant updated successfully"), "success")
      } else {
        await axios.post(`${API_URL}/api/crops`, plantForm)
        showNotification(t("Plant added successfully"), "success")
      }

      // Refresh plants
      const res = await axios.get(`${API_URL}/api/crops`)
      setPlants(res.data)

      // Reset form
      setPlantForm({
        name: "",
        scientificName: "",
        description: "",
        plantType: "",
        waterRequirements: "medium",
        growthCycle: "annual",
        region: "",
        plantMonth: "", // Changed from plantDate
        cropCoefficients: {
          initial: 0.3,
          mid: 1.0,
          end: 0.7,
        },
        growthStages: {
          initialDays: 20,
          developmentDays: 30,
          midSeasonDays: 40,
          lateSeasonDays: 30,
        },
        totalDays: 120,
      })
      setIsEditing(false)
      setEditingId(null)
    } catch (err) {
      console.error(err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const handleCropTypeSubmit = async (e) => {
    e.preventDefault()

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/crop-types/${editingId}`, cropTypeForm)
        showNotification(t("Crop type updated successfully"), "success")
      } else {
        await axios.post(`${API_URL}/api/crop-types`, cropTypeForm)
        showNotification(t("Crop type added successfully"), "success")
      }

      // Refresh crop types
      const res = await axios.get(`${API_URL}/api/crop-types`)
      setCropTypes(res.data)

      // Reset form
      setCropTypeForm({
        name: "",
        code: "",
        description: "",
      })
      setIsEditing(false)
      setEditingId(null)
    } catch (err) {
      console.error(err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  // Handle edit and delete
  const handleEdit = (id, type) => {
    setIsEditing(true)
    setEditingId(id)

    switch (type) {
      case "country":
        const country = countries.find((c) => c._id === id)
        setCountryForm({ name: country.name, code: country.code })
        break
      case "district":
        const district = districts.find((d) => d._id === id)
        setDistrictForm({
          name: district.name,
          country: district.country._id,
          coordinates: district.location.coordinates,
        })
        break
      case "soilType":
        const soilType = soilTypes.find((s) => s._id === id)
        setSoilTypeForm({
          name: soilType.name,
          description: soilType.description || "",
          properties: {
            texture: soilType.properties?.texture || "loamy",
            drainageRate: soilType.properties?.drainageRate || "moderate",
            waterHoldingCapacity: soilType.properties?.waterHoldingCapacity || "medium",
            organicMatterContent: soilType.properties?.organicMatterContent || "medium",
          },
        })
        break
      case "waterSource":
        const waterSource = waterSources.find((w) => w._id === id)
        setWaterSourceForm({
          name: waterSource.name,
          description: waterSource.description || "",
          type: waterSource.type,
          properties: {
            quality: waterSource.properties?.quality || "good",
            reliability: waterSource.properties?.reliability || "reliable",
            seasonality: waterSource.properties?.seasonality || "year-round",
          },
        })
        break
      case "cropType":
        const cropType = cropTypes.find((ct) => ct._id === id)
        setCropTypeForm({
          name: cropType.name,
          code: cropType.code,
          description: cropType.description || "",
        })
        break
      case "plant":
        const plant = plants.find((p) => p._id === id)
        setPlantForm({
          name: plant.name,
          scientificName: plant.scientificName || "",
          description: plant.description || "",
          cropType: plant.cropType?._id || "",
          waterRequirements: plant.waterRequirements || "medium",
          growthCycle: plant.growthCycle || "annual",
          region: plant.region || "",
          plantMonth: plant.plantMonth || "", // Changed from plantDate
          cropCoefficients: {
            initial: plant.cropCoefficients?.initial || 0.3,
            mid: plant.cropCoefficients?.mid || 1.0,
            end: plant.cropCoefficients?.end || 0.7,
          },
          growthStages: {
            initialDays: plant.growthStages?.initialDays || 20,
            developmentDays: plant.growthStages?.developmentDays || 30,
            midSeasonDays: plant.growthStages?.midSeasonDays || 40,
            lateSeasonDays: plant.growthStages?.lateSeasonDays || 30,
          },
          totalDays: plant.totalDays || 120,
        })
        break
      default:
        break
    }
  }

  const handleDelete = async (id, type) => {
    if (!window.confirm(t("Are you sure you want to delete this item?"))) {
      return
    }

    try {
      switch (type) {
        case "country":
          await axios.delete(`${API_URL}/api/locations/countries/${id}`)
          setCountries(countries.filter((c) => c._id !== id))
          showNotification(t("Country deleted successfully"), "success")
          break
        case "district":
          await axios.delete(`${API_URL}/api/locations/districts/${id}`)
          setDistricts(districts.filter((d) => d._id !== id))
          showNotification(t("District deleted successfully"), "success")
          break
        case "soilType":
          await axios.delete(`${API_URL}/api/soil-types/${id}`)
          setSoilTypes(soilTypes.filter((s) => s._id !== id))
          showNotification(t("Soil type deleted successfully"), "success")
          break
        case "waterSource":
          await axios.delete(`${API_URL}/api/water-sources/${id}`)
          setWaterSources(waterSources.filter((w) => w._id !== id))
          showNotification(t("Water source deleted successfully"), "success")
          break
        case "cropType":
          await axios.delete(`${API_URL}/api/crop-types/${id}`)
          setCropTypes(cropTypes.filter((ct) => ct._id !== id))
          showNotification(t("Crop type deleted successfully"), "success")
          break
        case "plant":
          await axios.delete(`${API_URL}/api/crops/${id}`)
          setPlants(plants.filter((p) => p._id !== id))
          showNotification(t("Plant deleted successfully"), "success")
          break
        default:
          break
      }
    } catch (err) {
      console.error(err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const cancelEdit = () => {
    setIsEditing(false)
    setEditingId(null)

    // Reset forms
    setCountryForm({ name: "", code: "" })
    setDistrictForm({ name: "", country: "", coordinates: [0, 0] })
    setSoilTypeForm({
      name: "",
      description: "",
      properties: {
        texture: "loamy",
        drainageRate: "moderate",
        waterHoldingCapacity: "medium",
        organicMatterContent: "medium",
      },
    })
    setWaterSourceForm({
      name: "",
      description: "",
      type: "surface",
      properties: {
        quality: "good",
        reliability: "reliable",
        seasonality: "year-round",
      },
    })
    setCropTypeForm({
      name: "",
      code: "",
      description: "",
    })
    setPlantForm({
      name: "",
      scientificName: "",
      description: "",
      cropType: "",
      waterRequirements: "medium",
      growthCycle: "annual",
      region: "",
      plantMonth: "", // Changed from plantDate
      cropCoefficients: {
        initial: 0.3,
        mid: 1.0,
        end: 0.7,
      },
      growthStages: {
        initialDays: 20,
        developmentDays: 30,
        midSeasonDays: 40,
        lateSeasonDays: 30,
      },
      totalDays: 120,
    })
  }

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Check file type
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls") && !file.name.endsWith(".csv")) {
      showNotification(t("Please upload an Excel or CSV file"), "error")
      return
    }

    const formData = new FormData()
    formData.append("file", file)

    setUploading(true)

    try {
      let endpoint
      if (activeTab === "countries") {
        endpoint = `${API_URL}/api/admin/upload/countries`
      } else if (activeTab === "districts") {
        endpoint = `${API_URL}/api/admin/upload/districts`
      }

      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      showNotification(t("Data uploaded successfully"), "success")

      // Refresh data
      if (activeTab === "countries") {
        const countriesRes = await axios.get(`${API_URL}/api/locations/countries`)
        setCountries(countriesRes.data)
      } else if (activeTab === "districts") {
        const districtsRes = await axios.get(`${API_URL}/api/locations/districts/all`)
        setDistricts(districtsRes.data)
      }
    } catch (err) {
      console.error(err)
      const errorMsg = err.response?.data?.msg || "Upload failed"
      showNotification(t(errorMsg), "error")
    } finally {
      setUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  // Download template
  const downloadTemplate = (type) => {
    // Create template data
    let headers = []
    let data = []

    if (type === "countries") {
      headers = ["name", "code"]
      data = [
        ["United States", "US"],
        ["Canada", "CA"],
        ["United Kingdom", "UK"],
      ]
    } else if (type === "districts") {
      headers = ["name", "country_code", "longitude", "latitude"]
      data = [
        ["New York", "US", "-74.0060", "40.7128"],
        ["Los Angeles", "US", "-118.2437", "34.0522"],
        ["Toronto", "CA", "-79.3832", "43.6532"],
      ]
    }

    // Create CSV content
    const csvContent = [headers.join(","), ...data.map((row) => row.join(","))].join("\n")

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${type}_template.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="master-data-container" dir={direction}>
      <div className="master-data-header">
        <h1>{t("Master Data Management")}</h1>
        <p className="subtitle">{t("Manage reference data for the application")}</p>
      </div>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === "countries" ? "active" : ""}`}
          onClick={() => setActiveTab("countries")}
        >
          <i className="fas fa-globe"></i> {t("Countries")}
        </button>
        <button
          className={`tab-button ${activeTab === "districts" ? "active" : ""}`}
          onClick={() => setActiveTab("districts")}
        >
          <i className="fas fa-map-marker-alt"></i> {t("Districts")}
        </button>
        <button
          className={`tab-button ${activeTab === "soilTypes" ? "active" : ""}`}
          onClick={() => setActiveTab("soilTypes")}
        >
          <i className="fas fa-mountain"></i> {t("Soil Types")}
        </button>
        <button
          className={`tab-button ${activeTab === "waterSources" ? "active" : ""}`}
          onClick={() => setActiveTab("waterSources")}
        >
          <i className="fas fa-water"></i> {t("Water Sources")}
        </button>
        <button
          className={`tab-button ${activeTab === "cropTypes" ? "active" : ""}`}
          onClick={() => setActiveTab("cropTypes")}
        >
          <i className="fas fa-seedling"></i> {t("Crop Types")}
        </button>
        <button
          className={`tab-button ${activeTab === "plants" ? "active" : ""}`}
          onClick={() => setActiveTab("plants")}
        >
          <i className="fas fa-leaf"></i> {t("Plants")}
        </button>
      </div>

      <div className="tab-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>{t("Loading data...")}</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i> {t(error)}
          </div>
        ) : (
          <>
            {/* Countries Tab */}
{activeTab === "countries" && (
  <div className="countries-tab">
    {/* Bulk Upload Section */}
    <div className="bulk-upload-section">
      <h3>{t("Bulk Upload")}</h3>
      <div className="upload-controls">
        <button className="template-button" onClick={() => downloadTemplate("countries")}>
          <i className="fas fa-download"></i> {t("Download Template")}
        </button>
        <div className="file-upload-container">
          <input
            type="file"
            id="country-file-upload"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls,.csv"
            className="file-input"
            disabled={uploading}
          />
          <label
            htmlFor="country-file-upload"
            className={`file-upload-button ${uploading ? "disabled" : ""}`}
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> {t("Uploading...")}
              </>
            ) : (
              <>
                <i className="fas fa-upload"></i> {t("Upload Excel/CSV")}
              </>
            )}
          </label>
        </div>
      </div>
      <p className="upload-note">
        <i className="fas fa-info-circle"></i> {t("Upload an Excel or CSV file with country data")}
      </p>
    </div>

    {/* Form Section */}
    <div className="form-section">
      <h2>{isEditing ? t("Edit Country") : t("Add Country")}</h2>
      <form onSubmit={handleCountrySubmit}>
        <div className="form-group">
          <label htmlFor="name">{t("Country Name")}</label>
          <input
            type="text"
            id="name"
            name="name"
            value={countryForm.name}
            onChange={handleCountryChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="code">{t("Country Code")}</label>
          <input
            type="text"
            id="code"
            name="code"
            value={countryForm.code}
            onChange={handleCountryChange}
            required
          />
        </div>
        <div className="form-actions">
          {isEditing && (
            <button type="button" className="cancel-button" onClick={cancelEdit}>
              {t("Cancel")}
            </button>
          )}
          <button type="submit" className="submit-button">
            {isEditing ? t("Update") : t("Add")}
          </button>
        </div>
      </form>
    </div>

    {/* Data Section */}
    <div className="data-section">
      <div
        className="data-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <h2>{t("Countries List")}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input
            type="text"
            placeholder={t("Search Country")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ padding: "6px", maxWidth: "250px" }}
          />
          <span style={{ fontWeight: "bold" }}>
            {t("Total")}: {filteredCountries.length} / {countries.length}
          </span>
        </div>
      </div>

      {/* Filtered Table */}
      {filteredCountries.length === 0 ? (
        <p className="no-data">{t("No countries found")}</p>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Name")}</th>
                <th>{t("Code")}</th>
                <th>{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCountries.map((country) => (
                <tr key={country._id}>
                  <td>{country.name}</td>
                  <td>{country.code}</td>
                  <td>
                    <div className="table-actions">
                      <button className="edit-button" onClick={() => handleEdit(country._id, "country")}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button className="delete-button" onClick={() => handleDelete(country._id, "country")}>
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
)}



            {/* Districts Tab */}
{activeTab === "districts" && (
  <div className="districts-tab">
    {/* Bulk Upload Section */}
    <div className="bulk-upload-section">
      <h3>{t("Bulk Upload")}</h3>
      <div className="upload-controls">
        <button className="template-button" onClick={() => downloadTemplate("districts")}>
          <i className="fas fa-download"></i> {t("Download Template")}
        </button>
        <div className="file-upload-container">
          <input
            type="file"
            id="district-file-upload"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls,.csv"
            className="file-input"
            disabled={uploading}
          />
          <label
            htmlFor="district-file-upload"
            className={`file-upload-button ${uploading ? "disabled" : ""}`}
          >
            {uploading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> {t("Uploading...")}
              </>
            ) : (
              <>
                <i className="fas fa-upload"></i> {t("Upload Excel/CSV")}
              </>
            )}
          </label>
        </div>
      </div>
      <p className="upload-note">
        <i className="fas fa-info-circle"></i> {t("Upload an Excel or CSV file with district data")}
      </p>
    </div>

    {/* Form Section */}
    <div className="form-section">
      <h2>{isEditing ? t("Edit District") : t("Add District")}</h2>
      <form onSubmit={handleDistrictSubmit}>
        <div className="form-group">
          <label htmlFor="name">{t("District Name")}</label>
          <input
            type="text"
            id="name"
            name="name"
            value={districtForm.name}
            onChange={handleDistrictChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="country">{t("Country")}</label>
          <select
            id="country"
            name="country"
            value={districtForm.country}
            onChange={handleDistrictChange}
            required
          >
            <option value="">{t("Select a country")}</option>
            {countries.map((country) => (
              <option key={country._id} value={country._id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="longitude">{t("Longitude")}</label>
            <input
              type="number"
              id="longitude"
              name="longitude"
              value={districtForm.coordinates[0]}
              onChange={handleDistrictChange}
              step="0.000001"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="latitude">{t("Latitude")}</label>
            <input
              type="number"
              id="latitude"
              name="latitude"
              value={districtForm.coordinates[1]}
              onChange={handleDistrictChange}
              step="0.000001"
              required
            />
          </div>
        </div>
        <div className="form-actions">
          {isEditing && (
            <button type="button" className="cancel-button" onClick={cancelEdit}>
              {t("Cancel")}
            </button>
          )}
          <button type="submit" className="submit-button">
            {isEditing ? t("Update") : t("Add")}
          </button>
        </div>
      </form>
    </div>

    {/* Data Section */}
    <div className="data-section">
      <div
        className="data-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <h2>{t("Districts List")}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <input
            type="text"
            placeholder={t("Search District")}
            value={searchDistrict}
            onChange={(e) => setSearchDistrict(e.target.value)}
            className="search-input"
            style={{ padding: "6px", maxWidth: "250px" }}
          />
          <span style={{ fontWeight: "bold" }}>
            {t("Total")}: {filteredDistricts.length} / {districts.length}
          </span>
        </div>
      </div>

      {/* Filtered Table */}
      {filteredDistricts.length === 0 ? (
        <p className="no-data">{t("No districts found")}</p>
      ) : (
        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t("Name")}</th>
                <th>{t("Country")}</th>
                <th>{t("Coordinates")}</th>
                <th>{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDistricts.map((district) => (
                <tr key={district._id}>
                  <td>{district.name}</td>
                  <td>{district.country?.name}</td>
                  <td>
                    {district.location?.coordinates[0].toFixed(6)},{" "}
                    {district.location?.coordinates[1].toFixed(6)}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="edit-button" onClick={() => handleEdit(district._id, "district")}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleDelete(district._id, "district")}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
)}


            {/* Soil Types Tab */}
            {activeTab === "soilTypes" && (
              <div className="soil-types-tab">
                <div className="form-section">
                  <h2>{isEditing ? t("Edit Soil Type") : t("Add Soil Type")}</h2>
                  <form onSubmit={handleSoilTypeSubmit}>
                    <div className="form-group">
                      <label htmlFor="name">{t("Soil Type Name")}</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={soilTypeForm.name}
                        onChange={handleSoilTypeChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="description">{t("Description")}</label>
                      <textarea
                        id="description"
                        name="description"
                        value={soilTypeForm.description}
                        onChange={handleSoilTypeChange}
                        rows="3"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label htmlFor="texture">{t("Texture")}</label>
                      <select
                        id="texture"
                        name="properties.texture"
                        value={soilTypeForm.properties.texture}
                        onChange={handleSoilTypeChange}
                      >
                        <option value="sandy">{t("Sandy")}</option>
                        <option value="loamy">{t("Loamy")}</option>
                        <option value="clay">{t("Clay")}</option>
                        <option value="silty">{t("Silty")}</option>
                        <option value="peaty">{t("Peaty")}</option>
                        <option value="chalky">{t("Chalky")}</option>
                        <option value="mixed">{t("Mixed")}</option>
                      </select>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="drainageRate">{t("Drainage Rate")}</label>
                        <select
                          id="drainageRate"
                          name="properties.drainageRate"
                          value={soilTypeForm.properties.drainageRate}
                          onChange={handleSoilTypeChange}
                        >
                          <option value="very slow">{t("Very Slow")}</option>
                          <option value="slow">{t("Slow")}</option>
                          <option value="moderate">{t("Moderate")}</option>
                          <option value="fast">{t("Fast")}</option>
                          <option value="very fast">{t("Very Fast")}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="waterHoldingCapacity">{t("Water Holding Capacity")}</label>
                        <select
                          id="waterHoldingCapacity"
                          name="properties.waterHoldingCapacity"
                          value={soilTypeForm.properties.waterHoldingCapacity}
                          onChange={handleSoilTypeChange}
                        >
                          <option value="very low">{t("Very Low")}</option>
                          <option value="low">{t("Low")}</option>
                          <option value="medium">{t("Medium")}</option>
                          <option value="high">{t("High")}</option>
                          <option value="very high">{t("Very High")}</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="organicMatterContent">{t("Organic Matter Content")}</label>
                      <select
                        id="organicMatterContent"
                        name="properties.organicMatterContent"
                        value={soilTypeForm.properties.organicMatterContent}
                        onChange={handleSoilTypeChange}
                      >
                        <option value="very low">{t("Very Low")}</option>
                        <option value="low">{t("Low")}</option>
                        <option value="medium">{t("Medium")}</option>
                        <option value="high">{t("High")}</option>
                        <option value="very high">{t("Very High")}</option>
                      </select>
                    </div>
                    <div className="form-actions">
                      {isEditing && (
                        <button type="button" className="cancel-button" onClick={cancelEdit}>
                          {t("Cancel")}
                        </button>
                      )}
                      <button type="submit" className="submit-button">
                        {isEditing ? t("Update") : t("Add")}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="data-section">
                  <h2>{t("Soil Types List")}</h2>
                  {soilTypes.length === 0 ? (
                    <p className="no-data">{t("No soil types found")}</p>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>{t("Name")}</th>
                            <th>{t("Texture")}</th>
                            <th>{t("Water Holding")}</th>
                            <th>{t("Actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {soilTypes.map((soilType) => (
                            <tr key={soilType._id}>
                              <td>{soilType.name}</td>
                              <td>{t(soilType.properties?.texture || "N/A")}</td>
                              <td>{t(soilType.properties?.waterHoldingCapacity || "N/A")}</td>
                              <td>
                                <div className="table-actions">
                                  <button className="edit-button" onClick={() => handleEdit(soilType._id, "soilType")}>
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="delete-button"
                                    onClick={() => handleDelete(soilType._id, "soilType")}
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Water Sources Tab */}
            {activeTab === "waterSources" && (
              <div className="water-sources-tab">
                {/* Similar structure to soil types tab */}
                <div className="form-section">
                  <h2>{isEditing ? t("Edit Water Source") : t("Add Water Source")}</h2>
                  <form onSubmit={handleWaterSourceSubmit}>
                    <div className="form-group">
                      <label htmlFor="name">{t("Water Source Name")}</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={waterSourceForm.name}
                        onChange={handleWaterSourceChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="description">{t("Description")}</label>
                      <textarea
                        id="description"
                        name="description"
                        value={waterSourceForm.description}
                        onChange={handleWaterSourceChange}
                        rows="3"
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label htmlFor="type">{t("Type")}</label>
                      <select
                        id="type"
                        name="type"
                        value={waterSourceForm.type}
                        onChange={handleWaterSourceChange}
                        required
                      >
                        <option value="surface">{t("Surface Water")}</option>
                        <option value="groundwater">{t("Groundwater")}</option>
                        <option value="rainwater">{t("Rainwater")}</option>
                        <option value="treated">{t("Treated Water")}</option>
                        <option value="other">{t("Other")}</option>
                      </select>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="quality">{t("Water Quality")}</label>
                        <select
                          id="quality"
                          name="properties.quality"
                          value={waterSourceForm.properties.quality}
                          onChange={handleWaterSourceChange}
                        >
                          <option value="excellent">{t("Excellent")}</option>
                          <option value="good">{t("Good")}</option>
                          <option value="fair">{t("Fair")}</option>
                          <option value="poor">{t("Poor")}</option>
                          <option value="unknown">{t("Unknown")}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="reliability">{t("Reliability")}</label>
                        <select
                          id="reliability"
                          name="properties.reliability"
                          value={waterSourceForm.properties.reliability}
                          onChange={handleWaterSourceChange}
                        >
                          <option value="very reliable">{t("Very Reliable")}</option>
                          <option value="reliable">{t("Reliable")}</option>
                          <option value="somewhat reliable">{t("Somewhat Reliable")}</option>
                          <option value="unreliable">{t("Unreliable")}</option>
                          <option value="unknown">{t("Unknown")}</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="seasonality">{t("Seasonality")}</label>
                      <select
                        id="seasonality"
                        name="properties.seasonality"
                        value={waterSourceForm.properties.seasonality}
                        onChange={handleWaterSourceChange}
                      >
                        <option value="year-round">{t("Year-round")}</option>
                        <option value="seasonal">{t("Seasonal")}</option>
                        <option value="intermittent">{t("Intermittent")}</option>
                        <option value="unknown">{t("Unknown")}</option>
                      </select>
                    </div>
                    <div className="form-actions">
                      {isEditing && (
                        <button type="button" className="cancel-button" onClick={cancelEdit}>
                          {t("Cancel")}
                        </button>
                      )}
                      <button type="submit" className="submit-button">
                        {isEditing ? t("Update") : t("Add")}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="data-section">
                  <h2>{t("Water Sources List")}</h2>
                  {waterSources.length === 0 ? (
                    <p className="no-data">{t("No water sources found")}</p>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>{t("Name")}</th>
                            <th>{t("Type")}</th>
                            <th>{t("Quality")}</th>
                            <th>{t("Actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {waterSources.map((waterSource) => (
                            <tr key={waterSource._id}>
                              <td>{waterSource.name}</td>
                              <td>{t(waterSource.type)}</td>
                              <td>{t(waterSource.properties?.quality || "Unknown")}</td>
                              <td>
                                <div className="table-actions">
                                  <button
                                    className="edit-button"
                                    onClick={() => handleEdit(waterSource._id, "waterSource")}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="delete-button"
                                    onClick={() => handleDelete(waterSource._id, "waterSource")}
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Crop Types Tab */}
            {activeTab === "cropTypes" && (
              <div className="crop-types-tab">
                <div className="form-section">
                  <h2>{isEditing ? t("Edit Crop Type") : t("Add Crop Type")}</h2>
                  <form onSubmit={handleCropTypeSubmit}>
                    <div className="form-group">
                      <label htmlFor="name">{t("Crop Type Name")}</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={cropTypeForm.name}
                        onChange={handleCropTypeChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="code">{t("Crop Type Code")}</label>
                      <input
                        type="text"
                        id="code"
                        name="code"
                        value={cropTypeForm.code}
                        onChange={handleCropTypeChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="description">{t("Description")}</label>
                      <textarea
                        id="description"
                        name="description"
                        value={cropTypeForm.description}
                        onChange={handleCropTypeChange}
                        rows="3"
                      ></textarea>
                    </div>
                    <div className="form-actions">
                      {isEditing && (
                        <button type="button" className="cancel-button" onClick={cancelEdit}>
                          {t("Cancel")}
                        </button>
                      )}
                      <button type="submit" className="submit-button">
                        {isEditing ? t("Update") : t("Add")}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="data-section">
                  <h2>{t("Crop Types List")}</h2>
                  {cropTypes.length === 0 ? (
                    <p className="no-data">{t("No crop types found")}</p>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>{t("Name")}</th>
                            <th>{t("Code")}</th>
                            <th>{t("Description")}</th>
                            <th>{t("Actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cropTypes.map((cropType) => (
                            <tr key={cropType._id}>
                              <td>{cropType.name}</td>
                              <td>{cropType.code}</td>
                              <td>{cropType.description || t("No description")}</td>
                              <td>
                                <div className="table-actions">
                                  <button className="edit-button" onClick={() => handleEdit(cropType._id, "cropType")}>
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="delete-button"
                                    onClick={() => handleDelete(cropType._id, "cropType")}
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Plants Tab */}
            {activeTab === "plants" && (
              <div className="plants-tab">
                <div className="form-section">
                  <h2>{isEditing ? t("Edit Plant") : t("Add Plant")}</h2>
                  <form onSubmit={handlePlantSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">{t("Plant Name")}</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={plantForm.name}
                          onChange={handlePlantChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="scientificName">{t("Scientific Name")}</label>
                        <input
                          type="text"
                          id="scientificName"
                          name="scientificName"
                          value={plantForm.scientificName}
                          onChange={handlePlantChange}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="description">{t("Description")}</label>
                      <textarea
                        id="description"
                        name="description"
                        value={plantForm.description}
                        onChange={handlePlantChange}
                        rows="3"
                      ></textarea>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="cropType">{t("Crop Type")}</label>
                        <select
                          id="cropType"
                          name="cropType"
                          value={plantForm.cropType}
                          onChange={handlePlantChange}
                          required
                        >
                          <option value="">{t("Select a crop Type")}</option>
                          {plantTypes.map((type) => (
                            <option key={type._id} value={type._id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="region">{t("Region")}</label>
                        <input
                          type="text"
                          id="region"
                          name="region"
                          value={plantForm.region}
                          onChange={handlePlantChange}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="plantMonth">{t("Plant Month")}</label>
                        <select
                          id="plantMonth"
                          name="plantMonth"
                          value={plantForm.plantMonth || ""}
                          onChange={(e) => setPlantForm({ ...plantForm, plantMonth: e.target.value })}
                          required
                        >
                          <option value="">{t("Select Month")}</option>
                          <option value="1">{t("January")}</option>
                          <option value="2">{t("February")}</option>
                          <option value="3">{t("March")}</option>
                          <option value="4">{t("April")}</option>
                          <option value="5">{t("May")}</option>
                          <option value="6">{t("June")}</option>
                          <option value="7">{t("July")}</option>
                          <option value="8">{t("August")}</option>
                          <option value="9">{t("September")}</option>
                          <option value="10">{t("October")}</option>
                          <option value="11">{t("November")}</option>
                          <option value="12">{t("December")}</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="waterRequirements">{t("Water Requirements")}</label>
                        <select
                          id="waterRequirements"
                          name="waterRequirements"
                          value={plantForm.waterRequirements}
                          onChange={handlePlantChange}
                        >
                          <option value="very low">{t("Very Low")}</option>
                          <option value="low">{t("Low")}</option>
                          <option value="medium">{t("Medium")}</option>
                          <option value="high">{t("High")}</option>
                          <option value="very high">{t("Very High")}</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label htmlFor="growthCycle">{t("Growth Cycle")}</label>
                        <select
                          id="growthCycle"
                          name="growthCycle"
                          value={plantForm.growthCycle}
                          onChange={handlePlantChange}
                        >
                          <option value="annual">{t("Annual")}</option>
                          <option value="biennial">{t("Biennial")}</option>
                          <option value="perennial">{t("Perennial")}</option>
                        </select>
                      </div>
                    </div>

                    <h3>{t("Crop Coefficients")}</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="initialCoef">{t("Initial Stage")}</label>
                        <input
                          type="number"
                          id="initialCoef"
                          name="cropCoefficients.initial"
                          value={plantForm.cropCoefficients.initial}
                          onChange={handlePlantChange}
                          step="0.01"
                          min="0"
                          max="2"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="midCoef">{t("Mid-Season Stage")}</label>
                        <input
                          type="number"
                          id="midCoef"
                          name="cropCoefficients.mid"
                          value={plantForm.cropCoefficients.mid}
                          onChange={handlePlantChange}
                          step="0.01"
                          min="0"
                          max="2"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="endCoef">{t("End Stage")}</label>
                        <input
                          type="number"
                          id="endCoef"
                          name="cropCoefficients.end"
                          value={plantForm.cropCoefficients.end}
                          onChange={handlePlantChange}
                          step="0.01"
                          min="0"
                          max="2"
                        />
                      </div>
                    </div>

                    <h3>{t("Growth Stages (days)")}</h3>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="initialDays">{t("Initial")}</label>
                        <input
                          type="number"
                          id="initialDays"
                          name="growthStages.initialDays"
                          value={plantForm.growthStages.initialDays}
                          onChange={handlePlantChange}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="developmentDays">{t("Development")}</label>
                        <input
                          type="number"
                          id="developmentDays"
                          name="growthStages.developmentDays"
                          value={plantForm.growthStages.developmentDays}
                          onChange={handlePlantChange}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="midSeasonDays">{t("Mid-Season")}</label>
                        <input
                          type="number"
                          id="midSeasonDays"
                          name="growthStages.midSeasonDays"
                          value={plantForm.growthStages.midSeasonDays}
                          onChange={handlePlantChange}
                          min="0"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lateSeasonDays">{t("Late Season")}</label>
                        <input
                          type="number"
                          id="lateSeasonDays"
                          name="growthStages.lateSeasonDays"
                          value={plantForm.growthStages.lateSeasonDays}
                          onChange={handlePlantChange}
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="totalDays">{t("Total Days")}</label>
                      <input
                        type="number"
                        id="totalDays"
                        name="totalDays"
                        value={plantForm.totalDays}
                        readOnly
                        disabled
                      />
                    </div>

                    <div className="form-actions">
                      {isEditing && (
                        <button type="button" className="cancel-button" onClick={cancelEdit}>
                          {t("Cancel")}
                        </button>
                      )}
                      <button type="submit" className="submit-button">
                        {isEditing ? t("Update") : t("Add")}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="data-section">
                  <h2>{t("Plants List")}</h2>
                  {plants.length === 0 ? (
                    <p className="no-data">{t("No plants found")}</p>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>{t("Name")}</th>
                            <th>{t("Crop Type")}</th>
                            <th>{t("Region")}</th>
                            <th>{t("Water Requirements")}</th>
                            <th>{t("Actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plants.map((plant) => (
                            <tr key={plant._id}>
                              <td>{plant.name}</td>
                              <td>{plant.cropType?.name || t("Not specified")}</td>
                              <td>{plant.region || t("Not specified")}</td>
                              <td>{t(plant.waterRequirements || "Medium")}</td>
                              <td>
                                <div className="table-actions">
                                  <button className="edit-button" onClick={() => handleEdit(plant._id, "plant")}>
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button className="delete-button" onClick={() => handleDelete(plant._id, "plant")}>
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default MasterData
